import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { 
  AppBar, 
  Toolbar, 
  Button, 
  Container, 
  Box, 
  useTheme,
  useMediaQuery,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import Footer from './Footer';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';

const PublicLayout: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const { t, i18n } = useTranslation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path: string) => {
    if (path.startsWith('#')) {
      const element = document.getElementById(path.substring(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(path);
    }
  };

  // Close mobile menu when location changes (like Layout.tsx)
  React.useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const navItems = [
    { text: t('public-layout.nav.features'), path: '/features' },
    { text: t('public-layout.nav.how-it-works'), path: '/how-it-works' },
    { text: t('public-layout.nav.about'), path: '/about' },
    // { text: t('public-layout.nav.pricing'), path: '/pricing' },
    { text: t('public-layout.nav.contact'), path: '/contact' }
  ];

  React.useEffect(() => {
    document.documentElement.dir = i18n.language === 'en' ? 'ltr' : 'rtl';
  }, [i18n.language]);

  const drawer = (
    <Box 
      sx={{ textAlign: 'center', py: 2 }}
      role="presentation"
    >
      <Box 
        onClick={() => handleNavigation('/')}
        sx={{ cursor: 'pointer', display: 'block' }}
      >
        <img 
          src="/logo.png" 
          alt="Fushflow Logo" 
          className="header-logo" 
          style={{ 
            height: isMobile ? 60 : 80, 
            margin: '16px auto',
            display: 'block'
          }} 
        />
      </Box>
      <Divider sx={{ my: 2 }} />
      <List sx={{ px: 2 }}>
        <ListItemButton 
          onClick={() => handleNavigation('/')}
          sx={{ 
            borderRadius: 1, 
            mb: 1,
            py: isMobile ? 1.5 : 2
          }}
        >
          <ListItemText 
            primary={t('public-layout.nav.home')} 
            sx={{ 
              '& .MuiListItemText-primary': {
                fontSize: isMobile ? '1rem' : '1.1rem',
                fontWeight: 500
              }
            }}
          />
        </ListItemButton>
        {navItems.map((item) => (
          <ListItemButton 
            key={item.text} 
            onClick={() => handleNavigation(item.path)}
            sx={{ 
              borderRadius: 1, 
              mb: 1,
              py: isMobile ? 1.5 : 2
            }}
          >
            <ListItemText 
              primary={item.text} 
              sx={{ 
                '& .MuiListItemText-primary': {
                  fontSize: isMobile ? '1rem' : '1.1rem',
                  fontWeight: 500
                }
              }}
            />
          </ListItemButton>
        ))}
        <Box sx={{ mt: 3, px: 1 }}>
          <Button 
            variant="outlined" 
            onClick={() => handleNavigation('/login')}
            sx={{ 
              width: '100%', 
              py: isMobile ? 1.5 : 2,
              fontSize: isMobile ? '0.95rem' : '1rem',
              mb: 2
            }}
          >
            {t('sign_in')}
          </Button>
          <Button 
            variant="contained" 
            onClick={() => handleNavigation('/signup')}
            sx={{ 
              width: '100%', 
              py: isMobile ? 1.5 : 2,
              fontSize: isMobile ? '0.95rem' : '1rem'
            }}
          >
            {t('sign_up')}
          </Button>
        </Box>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }} data-hook="public-layout-root">
      <AppBar position="fixed" color="default" elevation={1} data-hook="public-layout-appbar">
        <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Toolbar disableGutters data-hook="public-layout-toolbar">
            <Box 
              onClick={() => navigate('/')}
              sx={{ cursor: 'pointer' }}
            >
              <img 
                src="/logo.png" 
                alt="Fushflow Logo" 
                className="header-logo" 
                style={{ 
                  height: isMobile ? 60 : 80, 
                  marginRight: isMobile ? (i18n.language === 'he' ? 0 : 12) : 16,
                  marginLeft: isMobile ? (i18n.language === 'he' ? 12 : 0) : 0
                }} 
              />
            </Box>

            {isMobile ? (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                ml: i18n.language === 'he' ? 0 : 'auto',
                mr: i18n.language === 'he' ? 'auto' : 0,
                flexDirection: i18n.language === 'he' ? 'row-reverse' : 'row'
              }}>
                <LanguageSelector
                  size="small"
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        border: 'none',
                      },
                      '&:hover fieldset': {
                        border: 'none',
                      },
                      '&.Mui-focused fieldset': {
                        border: 'none',
                      },
                    },
                  }}
                  formDataHook="public-language-select-form"
                  selectDataHook="public-language-select"
                />
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge={i18n.language === 'he' ? 'start' : 'end'}
                  onClick={handleDrawerToggle}
                  sx={{ 
                    p: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                  data-hook="drawer-toggle-btn"
                >
                  <MenuIcon />
                </IconButton>
              </Box>
            ) : (
              <>
                <Box sx={{ 
                  display: 'flex', 
                  gap: { xs: 1, sm: 1.5, md: 2 }, 
                  flexGrow: 1,
                  justifyContent: i18n.language === 'he' ? 'flex-start' : 'flex-start',
                  mr: i18n.language === 'he' ? 4 : 0,
                  ml: i18n.language === 'he' ? 0 : 0,
                }}>
                  <Button
                    onClick={() => navigate('/')} 
                    sx={{ 
                      color: 'text.primary',
                      px: { xs: 1, sm: 1.5, md: 2 },
                      minWidth: 'auto',
                      fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
                      py: { xs: 1, sm: 1.2, md: 1.5 }
                    }} 
                    data-hook="nav-home-btn"
                  >
                    {t('public-layout.nav.home')}
                  </Button>
                  {navItems.map((item) => (
                    <Button
                      key={item.text}
                      onClick={() => handleNavigation(item.path)}
                      sx={{ 
                        color: 'text.primary',
                        px: { xs: 1, sm: 1.5, md: 2 },
                        minWidth: 'auto',
                        fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
                        py: { xs: 1, sm: 1.2, md: 1.5 }
                      }}
                      data-hook={`nav-${item.text.toLowerCase().replace(/\s/g, '-')}-btn`}
                    >
                      {item.text}
                    </Button>
                  ))}
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  gap: { xs: 1.5, sm: 2, md: 2.5 }, 
                  alignItems: 'center',
                  ml: i18n.language === 'he' ? 0 : 'auto',
                  mr: i18n.language === 'he' ? 'auto' : 0,
                }}>
                  <Button 
                    variant="outlined" 
                    onClick={() => navigate('/login')}
                    sx={{ 
                      px: { xs: 2, sm: 2.5, md: 3 }, 
                      py: { xs: 1, sm: 1.2, md: 1.2 }, 
                      fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.95rem' }
                    }}
                    data-hook="sign-in-btn"
                  >
                    {t('sign_in')}
                  </Button>
                  <Button 
                    variant="contained" 
                    onClick={() => navigate('/signup')}
                    sx={{ 
                      px: { xs: 2, sm: 2.5, md: 3 }, 
                      py: { xs: 1, sm: 1.2, md: 1.2 }, 
                      fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.95rem' }
                    }}
                    data-hook="sign-up-btn"
                  >
                    {t('sign_up')}
                  </Button>
                </Box>
                <Box sx={{ 
                  ml: i18n.language === 'he' ? 0 : 4,
                  mr: i18n.language === 'he' ? 4 : 0,
                }}>
                  <LanguageSelector
                    size="small"
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          border: 'none',
                        },
                        '&:hover fieldset': {
                          border: 'none',
                        },
                        '&.Mui-focused fieldset': {
                          border: 'none',
                        },
                      },
                    }}
                    formDataHook="public-language-select-form"
                    selectDataHook="public-language-select"
                  />
                </Box>
              </>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Box component="nav">
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{
            keepMounted: false,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: { xs: 280, sm: 320 },
              maxWidth: '85vw'
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box component="main" sx={{ flexGrow: 1, pt: 0 }}>
        <Outlet />
      </Box>
      <Footer />
    </Box>
  );
};

export default PublicLayout; 